import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { ListingCardAddress } from '../ListingCard/types';
import useIsDesktop from '../BuildingModal/BuildingView/useIsDesktop';
import { cardListingPath } from './listingSlug';

/**
 * Marks a history entry this app pushed to open a listing. Read on close to
 * decide whether popping is safe. It lives on the entry rather than in a ref
 * because a ref only ever describes the latest navigation: open a related
 * listing from a listing and close it, and the ref has been overwritten by the
 * POP back onto the first one, which then closes to the map instead of to the
 * surface it came from.
 */
export type ListingLocationState = { pushedByApp?: boolean };

/**
 * Returns the canonical "open this one listing" navigator (JAA-345), the single
 * place that decides how a listing opens. Desktop (>=1024px) opens the
 * single-listing route in a new tab, so the surface it was opened from keeps its
 * exact state and the user can keep browsing; mobile has no background tabs, so
 * it pushes in the shell, where the view renders as a modal layer over the
 * still-mounted surface (map, pane, search, scroll) and closing returns to it.
 * Accepts the card listing itself so it can be handed straight to a card's
 * `onOpen`.
 *
 * Call it synchronously from the user's gesture — a `window.open` deferred past
 * an await is blocked as a popup.
 */
export const useOpenListing = () => {
  const history = useHistory();
  const isDesktop = useIsDesktop();
  return useCallback(
    (listing: {
      _id: string;
      address?: Partial<ListingCardAddress>;
      area_interest_id?: string;
    }) => {
      const path = cardListingPath(listing);
      // A card can sit inside a layer opened from the listing itself (the chat
      // drafted off it), where re-opening only grows history or duplicates a tab.
      if (history.location.pathname === path) return;
      if (isDesktop) {
        window.open(path, '_blank', 'noopener');
        return;
      }
      history.push(path, { pushedByApp: true } as ListingLocationState);
    },
    [history, isDesktop],
  );
};
